/**
 * Site-scope strip at the top of the chat panel.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 *
 * Shows the active scope ("Asking about: {site name}" or "Portfolio-wide")
 * with an X button to clear when scoped to a site. When the X is
 * clicked, the parent updates the scopeOverride slice state so the
 * chat panel falls back to portfolio scope until the user navigates
 * back to a site route OR clicks the "Re-scope to {site}" pill.
 */
import React from 'react';
import { Crosshair, Globe2, X } from 'lucide-react';

interface Props {
  /** The resolved scope — either a site name or null for portfolio. */
  scopeLabel: string | null;
  /** True when the scope was auto-detected from the active route. */
  isAutoDetected: boolean;
  /** Available when isAutoDetected is false + a site is at the active route. */
  routeScopeLabel?: string | null;
  onClearScope: () => void;
  onRestoreRouteScope?: () => void;
}

export function ChatSiteScopeHeader({
  scopeLabel,
  isAutoDetected,
  routeScopeLabel,
  onClearScope,
  onRestoreRouteScope,
}: Props): React.JSX.Element {
  if (scopeLabel) {
    return (
      <div
        className="flex items-center justify-between px-3 py-2 border-b-2 border-border bg-primary/5"
        data-testid="chat-scope-header-site"
      >
        <div className="flex items-center gap-2 text-text-primary min-w-0">
          <Crosshair className="w-3.5 h-3.5 text-primary shrink-0" aria-hidden="true" />
          <span className="text-[9px] uppercase tracking-widest font-bold text-text-muted">
            Asking about
          </span>
          <span className="text-[10px] uppercase tracking-widest text-text-primary truncate">
            {scopeLabel}
          </span>
          {isAutoDetected && (
            <span className="text-[8px] uppercase tracking-widest text-text-muted">(auto)</span>
          )}
        </div>
        <button
          type="button"
          onClick={onClearScope}
          aria-label="Clear site scope and ask portfolio-wide"
          className="text-text-muted hover:text-text-primary"
          data-testid="chat-scope-clear"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <div
      className="flex items-center justify-between px-3 py-2 border-b-2 border-border bg-background"
      data-testid="chat-scope-header-portfolio"
    >
      <div className="flex items-center gap-2 text-text-secondary">
        <Globe2 className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="text-[9px] uppercase tracking-widest font-bold">
          Portfolio-wide
        </span>
      </div>
      {routeScopeLabel && onRestoreRouteScope && (
        <button
          type="button"
          onClick={onRestoreRouteScope}
          className="text-[9px] uppercase tracking-widest text-text-muted hover:text-primary border border-border-subtle px-2 py-0.5"
          data-testid="chat-scope-restore"
        >
          Re-scope to {routeScopeLabel}
        </button>
      )}
    </div>
  );
}
